import { useState, useEffect } from 'react';
import axios from 'axios';
import '../styles.css';

import { API_URL } from '../config';

function ManageSubscription() {
  const [isPremium, setIsPremium] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchSubscriptionStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const response = await axios.get(`${API_URL}/api/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      setIsPremium(response.data.isPremium);
    } catch (err) {
      setError('Failed to load subscription status');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Premium subscription? You will lose access to:\n\n• AI Assistant\n• Custom Questions\n• Answer Search & Editing\n• Priority Support\n\nThis action cannot be undone.')) {
      return;
    }
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/user/subscription`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Refresh subscription status from server to get accurate state
      await fetchSubscriptionStatus();
      
      setSuccessMessage('Your subscription has been cancelled. You will retain Premium features until the end of your current billing period.');
      setError('');
      
      // Trigger page refresh to update all components (Dashboard, Layout, etc.)
      setTimeout(() => {
        window.location.reload();
      }, 1500); // Give user time to see success message
    } catch (err) {
      setError('Failed to cancel subscription: ' + (err.response?.data?.error || err.message));
      setSuccessMessage('');
      setIsLoading(false);
    }
  };

  const handleUpgrade = () => {
    window.location.href = '/upgrade';
  };

  if (isLoading) {
    return (
      <div className="container">
        <div className="spinner">Loading subscription information...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <nav className="dashboard-nav">
        <button onClick={() => window.location.href = '/dashboard'}>Dashboard</button>
        <button onClick={() => window.location.href = '/question-of-the-day'}>Question of the Day</button>
        <button onClick={() => window.location.href = '/ai-assistant'}>AI Assistant</button>
        <button onClick={() => {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }}>
          Logout
        </button>
      </nav>

      <h1>Manage Subscription</h1>
      
      {error && <div className="error">{error}</div>}
      {successMessage && <div className="success">{successMessage}</div>}

      <div className="subscription-status" style={{ 
        marginBottom: '40px',
        padding: '20px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        backgroundColor: isPremium ? '#e8f5e8' : '#fff3cd'
      }}>
        <h2>Current Subscription Status</h2>
        <div style={{ 
          fontSize: '18px', 
          fontWeight: 'bold',
          color: isPremium ? '#155724' : '#856404',
          marginBottom: '10px'
        }}>
          {isPremium ? '✅ Premium Active' : '⚠️ Basic Account'}
        </div>
        <p style={{ marginBottom: '0' }}>
          {isPremium 
            ? 'You have access to all Premium features including AI Assistant, custom questions, and advanced search.'
            : 'Upgrade to Premium to unlock AI insights, custom questions, answer editing, and more!'
          }
        </p>
      </div>

      {isPremium ? (
        <div className="premium-section">
          <div className="features-included" style={{ 
            marginBottom: '30px',
            padding: '20px',
            border: '1px solid #d4edda',
            borderRadius: '8px',
            backgroundColor: '#f8fff9'
          }}>
            <h3>Your Premium Features Include:</h3>
            <ul style={{ marginBottom: '0' }}>
              <li>🤖 <strong>AI Assistant</strong> - Get personalized gift ideas, date suggestions, and relationship insights</li>
              <li>🔍 <strong>Advanced Search</strong> - Search and edit all your questions and answers</li>
              <li>➕ <strong>Custom Questions</strong> - Add your own questions to the rotation</li>
              <li>✏️ <strong>Edit & Delete</strong> - Modify or remove your answers and custom questions</li>
              <li>📊 <strong>Enhanced Analytics</strong> - Better insights into your relationship data</li>
              <li>🎯 <strong>Priority Support</strong> - Faster response times for help requests</li>
            </ul>
          </div>

          <div className="cancel-section" style={{ 
            padding: '20px',
            border: '1px solid #f8d7da',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa'
          }}>
            <h3>Cancel Subscription</h3>
            <p>If you need to cancel your Premium subscription, you can do so below. You'll continue to have access to Premium features until the end of your current billing period.</p>
            <button 
              onClick={handleCancelSubscription}
              className="btn-form-danger"
              disabled={isLoading}
            >
              {isLoading ? 'Processing...' : 'Cancel Subscription'}
            </button>
          </div>
        </div>
      ) : (
        <div className="upgrade-section">
          <div className="features-preview" style={{ 
            marginBottom: '30px',
            padding: '20px',
            border: '1px solid #ffeaa7',
            borderRadius: '8px',
            backgroundColor: '#fffbf0'
          }}>
            <h3>Upgrade to Premium and Get:</h3>
            <ul style={{ marginBottom: '20px' }}>
              <li>🤖 <strong>AI Assistant</strong> - Ask natural language questions about your wife</li>
              <li>🔍 <strong>Search & Edit</strong> - Find and modify your answers easily</li>
              <li>➕ <strong>Custom Questions</strong> - Create your own personalized questions</li>
              <li>📱 <strong>Enhanced Experience</strong> - More features and faster support</li>
            </ul>
            <div style={{ textAlign: 'center' }}>
              <button 
                onClick={handleUpgrade}
                style={{ 
                  padding: '15px 30px',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
              >
                Upgrade to Premium - $7.77/month
              </button>
            </div>
          </div>

          <div className="basic-limitations" style={{ 
            padding: '15px',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            backgroundColor: '#f8f9fa'
          }}>
            <h4>Current Basic Account Limitations:</h4>
            <ul style={{ marginBottom: '0', color: '#6c757d' }}>
              <li>No AI Assistant access</li>
              <li>Cannot create custom questions</li>
              <li>Limited search capabilities</li>
              <li>Cannot edit or delete answers</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default ManageSubscription;